import { useCallback, useEffect, useState } from 'react'
import type { TablesInsert, TablesUpdate } from '../../../supabase/functions/_shared/database.types'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { WorkoutLap, WorkoutStep } from '../types/database.tables'
import type { WorkoutWithData } from '../types/workout'
import { createDateRangeForQuery } from '../utils/dateUtils'

// Activity types are now stored directly as strings in the database
// No mapping needed - discipline equals activity_type

export interface UseScheduledWorkoutsReturn {
  workouts: WorkoutWithData[]
  isLoading: boolean
  error: string | null
  loadWorkouts: (startDate: Date, endDate: Date) => Promise<void>
  loadWorkoutById: (workoutId: string) => Promise<WorkoutWithData>
  loadWorkoutLaps: (workoutId: string) => Promise<WorkoutLap[]>
  loadWorkoutSteps: (workoutId: string) => Promise<WorkoutStep[]>
  createWorkout: (workout: WorkoutCreate) => Promise<WorkoutWithData>
  updateWorkout: (workoutId: string, workout: WorkoutUpdate) => Promise<void>
  deleteWorkout: (workoutId: string) => Promise<void>
}

export type WorkoutCreate = TablesInsert<'workouts'>
export type WorkoutUpdate = TablesUpdate<'workouts'>

/**
 * Hook to manage workouts for a specific user
 * @param targetUserId - Optional user ID to fetch workouts for (defaults to authenticated user)
 */
export const useWorkouts = (targetUserId?: string): UseScheduledWorkoutsReturn => {
  const { user } = useAuth()
  const effectiveUserId = targetUserId || user?.id
  const [workouts, setWorkouts] = useState<WorkoutWithData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Function to load scheduled workouts from database
  const loadWorkouts = useCallback(
    async (startDate: Date, endDate: Date): Promise<void> => {
      if (!effectiveUserId) {
        setWorkouts([])
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // Use timezone-aware date range to ensure all workouts in the week are loaded
        const dateRange = createDateRangeForQuery(startDate, endDate)

        const { data, error: dbError } = await supabase
          .from('workouts')
          .select(`
            *,
            workout_sessions(
              id,
              total_timer_time,
              device_model
            )
          `)
          .eq('user_id', effectiveUserId)
          .gte('scheduled_date', dateRange.startDate)
          .lt('scheduled_date', dateRange.endDate)
          .order('scheduled_date', { ascending: true })
          .order('order', { ascending: true })

        if (dbError) {
          console.error('Error loading scheduled workouts:', dbError)
          throw dbError
        }

        // Add calculated fields including executed total duration from workout_sessions
        const workoutsWithCalculatedFields = (data || []).map((workout) => {
          const discipline = workout.activity_type || 'unknown'

          // Calculate executed total duration from joined workout_sessions
          let executedTotalDurationSeconds: number | null = 0
          if (workout.workout_sessions && workout.workout_sessions.length > 0) {
            executedTotalDurationSeconds = workout.workout_sessions[0].total_timer_time
          }
          return {
            ...workout,
            discipline,
            executedTotalDurationSeconds,
            // Only set TSS if it's not already present (preserve existing TSS values)
            // tss: workout.tss ?? estimatedTSS
          } as WorkoutWithData
        })

        setWorkouts(workoutsWithCalculatedFields)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load scheduled workouts'
        console.error('Failed to load scheduled workouts:', err)
        setError(errorMessage)
        setWorkouts([])
      } finally {
        setIsLoading(false)
      }
    },
    [effectiveUserId]
  )

  // Function to load a single workout by ID
  const loadWorkoutById = useCallback(
    async (workoutId: string): Promise<WorkoutWithData> => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      try {
        const { data, error: dbError } = await supabase
          .from('workouts')
          .select(`
            *,
            workout_sessions!workout_id (
              id,
              total_timer_time,
              avg_stroke_distance
            )
          `)
          .eq('id', workoutId)
          .single()

        if (dbError) {
          console.error('Error loading workout:', dbError)
          throw dbError
        }

        if (!data) {
          throw new Error('Workout not found')
        }

        // Add calculated fields including executed total duration from workout_sessions
        const discipline = data.activity_type || 'unknown'

        // Calculate executed total duration from joined workout_sessions
        let executedTotalDurationSeconds: number | null = 0
        if (data.workout_sessions && Array.isArray(data.workout_sessions)) {
          // Sum all duration_seconds from completed workout_session entries
          executedTotalDurationSeconds = (data.workout_sessions as { total_timer_time: number }[])
            .filter((wd) => wd.total_timer_time)
            .reduce((total, wd) => total + wd.total_timer_time, 0)
        }
        if (executedTotalDurationSeconds === 0) {
          executedTotalDurationSeconds = null
        }

        const workoutWithCalculatedFields = {
          ...data,
          discipline,
          executedTotalDurationSeconds,
        } as WorkoutWithData

        return workoutWithCalculatedFields
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load workout'
        console.error('Failed to load workout:', err)
        throw new Error(errorMessage)
      }
    },
    [effectiveUserId]
  )

  // Function to load workout laps for a specific workout
  const loadWorkoutLaps = useCallback(
    async (workoutId: string): Promise<WorkoutLap[]> => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      try {
        // First, get the workout session for this workout
        const { data: workoutSessions, error: sessionError } = await supabase
          .from('workout_sessions')
          .select('id')
          .eq('workout_id', workoutId)
          .eq('user_id', effectiveUserId)

        if (sessionError) {
          console.error('Error loading workout sessions:', sessionError)
          throw sessionError
        }

        if (!workoutSessions || workoutSessions.length === 0) {
          console.log('No workout sessions found for workout:', workoutId)
          return []
        }

        // Get workout laps for all sessions of this workout
        const sessionIds = workoutSessions.map((session) => session.id)

        const { data: laps, error: lapsError } = await supabase
          .from('workout_laps')
          .select('*')
          .in('workout_session_id', sessionIds)
          .eq('user_id', effectiveUserId)
          .order('lap_number', { ascending: true })

        if (lapsError) {
          console.error('Error loading workout laps:', lapsError)
          throw lapsError
        }

        return laps || []
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load workout laps'
        console.error('Failed to load workout laps:', err)
        throw new Error(errorMessage)
      }
    },
    [effectiveUserId]
  )

  const loadWorkoutSteps = useCallback(
    async (workoutId: string): Promise<WorkoutStep[]> => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      const { data: steps, error: stepsError } = await supabase
        .from('workout_steps')
        .select('*')
        .eq('workout_id', workoutId)
        .eq('user_id', effectiveUserId)
        .order('step_index', { ascending: true })
      if (stepsError) {
        console.error('Error loading workout steps:', stepsError)
        throw stepsError
      }

      return steps || []
    },
    [effectiveUserId]
  )

  const createWorkout = useCallback(
    async (workoutData: WorkoutCreate): Promise<WorkoutWithData> => {
      console.log('createWorkout:', workoutData)
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      try {
        // Clean up workout data for database insertion
        const cleanedWorkout = { ...workoutData }
        cleanedWorkout.user_id = effectiveUserId
        delete (cleanedWorkout as any).discipline
        delete (cleanedWorkout as any).intensity
        delete (cleanedWorkout as any).template_id
        delete (cleanedWorkout as any).intervals
        delete (cleanedWorkout as any).tss
        delete (cleanedWorkout as any).time

        const { error: dbError } = await supabase.from('workouts').insert(cleanedWorkout).select()
        if (dbError) {
          console.error('Error persisting workouts to database:', dbError)
          throw dbError
        }

        // Add calculated fields
        const discipline = cleanedWorkout.activity_type || 'unknown'
        // const estimatedTSS = estimateTSS(discipline, cleanedWorkout.duration_minutes, cleanedWorkout.intensity);

        const createdWorkout = {
          ...workoutData,
          discipline,
          // Only set TSS if it's not already present (preserve existing TSS values)
          // tss: workoutData.tss ?? estimatedTSS
        } as WorkoutWithData
        setWorkouts((prev) => [createdWorkout, ...prev])
        return createdWorkout
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to persist workouts'
        console.error('Failed to persist workouts:', err)
        setError(errorMessage)
        throw err
      }
    },
    [effectiveUserId]
  )

  // Function to delete a workout from database
  const deleteWorkout = useCallback(
    async (workoutId: string): Promise<void> => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      try {
        console.log('Attempting to delete workout:', workoutId, 'for user:', effectiveUserId)

        const { error: dbError, count } = await supabase.from('workouts').delete({ count: 'exact' }).eq('id', workoutId)

        if (dbError) {
          console.error('Error deleting workout from database:', dbError)
          throw dbError
        }

        console.log('Delete operation result - count:', count)

        if (count === 0) {
          console.warn('No rows were deleted - workout may not exist or user may not have permission')
          throw new Error('Workout could not be deleted - it may not exist or you may not have permission')
        }

        console.log('Successfully deleted workout from database:', workoutId)

        // Update local state to remove the deleted workout
        setWorkouts((prev) => prev.filter((workout) => workout.id !== workoutId))
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete workout'
        console.error('Failed to delete workout:', err)
        setError(errorMessage)
        throw err
      }
    },
    [effectiveUserId]
  )

  // Function to update an existing workout in database
  const updateWorkout = useCallback(
    async (workoutId: string, workoutData: WorkoutUpdate): Promise<void> => {
      if (!effectiveUserId) {
        throw new Error('User not authenticated')
      }

      try {
        // Clean up workout data for database update
        const cleaned = { ...workoutData }

        // Set user_id
        cleaned.user_id = effectiveUserId

        // Remove calculated/frontend fields that shouldn't be stored
        delete (cleaned as any).discipline
        delete (cleaned as any).intensity
        delete (cleaned as any).template_id
        delete (cleaned as any).intervals
        delete (cleaned as any).tss
        delete (cleaned as any).workout_sessions

        const { data, error: dbError } = await supabase
          .from('workouts')
          .update(cleaned)
          .eq('id', workoutId)
          .select()
          .single()

        if (dbError) {
          console.error('Error updating workout in database:', dbError)
          throw dbError
        }

        console.log('Successfully updated workout in database:', data)

        // Add calculated fields
        const discipline = data.activity_type || 'unknown'
        // const estimatedTSS = estimateTSS(discipline, data.duration_minutes, data.intensity);

        const updatedWorkoutWithCalculatedFields = {
          ...data,
          discipline,
          // Only set TSS if it's not already present (preserve existing TSS values)
          // tss: data.tss ?? estimatedTSS
        }

        // Update local state with updated workout
        setWorkouts((prev) =>
          prev.map((workout) => (workout.id === workoutId ? updatedWorkoutWithCalculatedFields : workout))
        )
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update workout'
        console.error('Failed to update workout:', err)
        setError(errorMessage)
        throw err
      }
    },
    [effectiveUserId]
  )

  // Clear workouts when effectiveUserId changes
  useEffect(() => {
    if (!effectiveUserId) {
      setWorkouts([])
      setError(null)
    }
  }, [effectiveUserId])

  return {
    workouts,
    isLoading,
    error,
    loadWorkouts,
    loadWorkoutById,
    loadWorkoutLaps,
    loadWorkoutSteps,
    createWorkout,
    updateWorkout,
    deleteWorkout,
  }
}
